import { CodeArticleVersion, Languages } from '@livv/models';
import ArrowRightAlt from '@mui/icons-material/ArrowRightAlt';
import { Box, SxProps, Typography } from '@mui/material';
import { fromUnixTime } from 'date-fns';
import { Diff } from 'diff-match-patch';
import useTranslation from 'next-translate/useTranslation';
import { FC } from 'react';
import { getDate } from '@utils/date';
import { DIFF_MODE, DIFF_COLORS } from '@utils/diff';

interface MonoDiffProps {
    paragraphsDiffs: Diff[][];
    diffMode: DIFF_MODE;
    version: CodeArticleVersion;
}

const reorderDiffs = (diffs: Diff[], oppositeMode: DIFF_MODE) => {
    // include also opposite mode diffs to have same number of chars
    // on both sides and ensure parrallelism (hidden, at the end)
    return [
        ...diffs.filter(([diffMode]) => diffMode !== oppositeMode),
        ...diffs.filter(([diffMode]) => diffMode === oppositeMode),
    ];
};

const MonoDiff: FC<MonoDiffProps> = ({ paragraphsDiffs, diffMode, version }) => {
    const oppositeMode = diffMode === DIFF_MODE.INSERT ? DIFF_MODE.DELETE : DIFF_MODE.INSERT;
    const { lang } = useTranslation();
    const { t } = useTranslation('codeArticle');

    const getSx = (dm: DIFF_MODE): SxProps => {
        switch (dm) {
            case oppositeMode:
                return {
                    visibility: 'hidden',
                };
            case DIFF_MODE.DELETE:
                return {
                    backgroundColor: DIFF_COLORS.DELETE_BG,
                    color: DIFF_COLORS.DELETE_TEXT,
                    textDecoration: 'line-through',
                };
            case DIFF_MODE.INSERT:
                return {
                    backgroundColor: DIFF_COLORS.INSERT_BG,
                    color: DIFF_COLORS.INSERT_TEXT,
                };
            default:
                return {};
        }
    };
    const { dateDebut, dateFin } = version;

    const startDate = getDate(fromUnixTime(dateDebut.seconds), lang as Languages, true);
    const endDate =
        dateFin.seconds > Date.now() / 1000
            ? t('today')
            : getDate(fromUnixTime(dateFin.seconds), lang as Languages, true);

    return (
        <>
            <Typography
                sx={{
                    alignItems: 'center',
                    color: 'grey.400',
                    display: 'flex',
                    fontSize: (theme) => theme.spacing(1.5),
                    fontWeight: 'medium',
                    height: (theme) => theme.spacing(3),
                    mb: 2,
                }}
            >
                {startDate}
                <ArrowRightAlt fontSize="small" />
                {endDate}
            </Typography>
            {paragraphsDiffs.map((diffs, paragraphIndex) => {
                const reorderedDiffs = reorderDiffs(diffs, oppositeMode);

                return (
                    <Typography key={`diff-${diffMode}-para${paragraphIndex + 1}`} gutterBottom>
                        {reorderedDiffs.map((diff, diffIndex) => (
                            <Box
                                key={`diff-${diffMode}-para${paragraphIndex + 1}-elt${
                                    diffIndex + 1
                                }`}
                                component="span"
                                sx={getSx(diff[0])}
                            >
                                {diff[1]}
                            </Box>
                        ))}
                    </Typography>
                );
            })}
        </>
    );
};

export default MonoDiff;
